import React, { ReactNode } from 'react';

import {
  ColorValue,
  DimensionValue,
  Modal as ReactNativeModal,
  StatusBar,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useKeyboardAwareFooter } from 'components/ucl/keyboard-aware-footer';
import { KeyboardAwareView } from 'components/ucl/keyboard-aware-view';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { primitive, spacing } from 'styles/constants';

import { Footer } from './footer';
import { Header, HeaderProps } from './header';

const IOS_KEYBOARD_ACCESSORY_BACKGROUND_HEIGHT = spacing.$14;

export type ModalHeaderOpts = Omit<HeaderProps, 'children' | 'onCloseIconClick'>;

export type ModalProps = {
  isOpen: boolean;
  header: ModalHeaderOpts | ReactNode;
  footer?: ReactNode;
  footerVariant?: 'light' | 'dark';
  backgroundColor?: ColorValue;
  children: ReactNode;
  webSize?: {
    height?: DimensionValue | undefined;
    width?: DimensionValue | undefined;
    minWidth?: DimensionValue | undefined;
  };
  onClose: () => void;
  modalBodyStyle?: StyleProp<ViewStyle>;
  modalContentStyle?: ViewStyle;
  keyboardAwareFooter?: boolean;
};

export function InlineModal({
  isOpen,
  header,
  children,
  footer = null,
  webSize,
  backgroundColor = primitive.$mayoEggWhite,
  onClose,
  modalBodyStyle,
  modalContentStyle,
  keyboardAwareFooter = false,
}: ModalProps) {
  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const { top: safeAreaTop } = useSafeAreaInsets();
  const headerVariant =
    header && typeof header === 'object' && 'title' in header && header.variant
      ? header.variant
      : 'dark';

  const headerColor = headerVariant === 'dark' ? primitive.$bbqBrown : primitive.$mayoEggWhite;
  const modalTopInset = isDesktopWeb ? 0 : safeAreaTop || StatusBar.currentHeight || 0;
  const {
    bottomOffset: keyboardAwareFooterBottomOffset,
    isKeyboardAwareFooterEnabled,
    onFooterLayout: handleFooterLayout,
  } = useKeyboardAwareFooter({ enabled: keyboardAwareFooter });
  const keyboardAwareScrollContainerStyle = keyboardAwareFooterBottomOffset
    ? { paddingBottom: keyboardAwareFooterBottomOffset }
    : undefined;
  const footerContent = footer ? (
    <View onLayout={handleFooterLayout} style={styles.keyboardAwareFooter}>
      <Footer>{footer}</Footer>
    </View>
  ) : null;
  const finalFooter =
    footerContent && isKeyboardAwareFooterEnabled ? (
      <KeyboardStickyView style={styles.keyboardAwareFooter}>
        <View pointerEvents="none" style={styles.keyboardAccessoryFooterBackground} />
        {footerContent}
      </KeyboardStickyView>
    ) : (
      footerContent
    );

  return (
    <ReactNativeModal
      animationType={isDesktopWeb ? 'fade' : 'slide'}
      visible={isOpen}
      transparent={true}
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
    >
      <View style={{ height: modalTopInset, backgroundColor: headerColor }} />
      <TouchableWithoutFeedback accessible={false} onPress={onClose}>
        <View
          style={[
            styles.modalContainer,
            { backgroundColor: isDesktopWeb ? 'rgba(0,0,0,0.5)' : headerColor },
          ]}
          accessible={false}
        >
          <TouchableWithoutFeedback accessible={false} onPress={e => e.stopPropagation()}>
            <View
              style={[
                styles.modalContent,
                {
                  backgroundColor,
                  borderRadius: isDesktopWeb ? spacing.$3 : 0,
                },
                modalContentStyle,
                webSize &&
                  isDesktopWeb && {
                    height: webSize.height,
                    width: webSize.width,
                    minWidth: webSize.minWidth,
                    maxHeight: '90%',
                  },
              ]}
            >
              {header && typeof header === 'object' && 'title' in header ? (
                <Header {...header} onCloseIconClick={onClose} />
              ) : (
                (header as any)
              )}
              <KeyboardAwareView
                bottomOffset={keyboardAwareFooterBottomOffset}
                contentContainerStyle={keyboardAwareScrollContainerStyle}
                keyboardShouldPersistTaps="handled"
                withScroll
                style={[styles.modalBody, modalBodyStyle]}
                accessible={false}
              >
                {children}
              </KeyboardAwareView>
              {finalFooter}
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </ReactNativeModal>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 56,
  },
  modalContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    width: '100%',
    height: '100%',
  },
  modalBody: {
    paddingVertical: spacing.$3,
    paddingHorizontal: spacing.$4,
    flex: 1,
  },
  iconContainer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: primitive.$lightBrown,
    paddingVertical: spacing.$3,
    paddingHorizontal: spacing.$4,
  },
  keyboardAccessoryFooterBackground: {
    backgroundColor: primitive.$white,
    bottom: -IOS_KEYBOARD_ACCESSORY_BACKGROUND_HEIGHT,
    height: IOS_KEYBOARD_ACCESSORY_BACKGROUND_HEIGHT,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  keyboardAwareFooter: {
    width: '100%',
  },
});
